import { workflowRunsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import type {
  CostActual,
  CostActualResponse,
  CostApproverStatus,
  CostDecisionRequest,
  CostEstimate,
  CostEstimateListParams,
  CostHistoryPage,
  CostHistoryParams,
} from '../types/cost-estimate.types';

/**
 * Cost-estimate API (workflow-webapp via `workflowRunsClient`, `/workflow/v1`).
 * The DTO is already camelCase, so no wire mapping is needed.
 */

/** `history` / `approver-status` are backend routes, not runs: a URL like
 *  `/cost-approvals/history` must read as "no estimate", never as that route's body. */
function isReservedId(workflowId: string): boolean {
  return endpoints.costEstimates.reservedIds.includes(workflowId);
}

/** Latest estimate for a run, or null when the gate has not run for it (404). */
export async function fetchCostEstimate(workflowId: string): Promise<CostEstimate | null> {
  if (isReservedId(workflowId)) return null;
  try {
    const res = await workflowRunsClient.get(endpoints.costEstimates.byWorkflow(workflowId));
    // 204 = no estimate for this run (yet); older backends answer 404 for the same case.
    if (res.status === 204) return null;
    return await res.json<CostEstimate>();
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/** Actual LLM spend recorded for a run so far, or null when nothing is recorded yet (204 / 404). */
export async function fetchCostActual(workflowId: string): Promise<CostActual | null> {
  if (isReservedId(workflowId)) return null;
  try {
    const res = await workflowRunsClient.get(endpoints.costEstimates.actual(workflowId));
    if (res.status === 204) return null;
    const raw = await res.json<CostActualResponse>();
    // The backend's sums are nullable (no rows with a recorded cost yet); a missing total or
    // enrichment cost reads as $0, the optional parts stay null so their breakdown line hides.
    return {
      ...raw,
      enrichmentUsd: raw.enrichmentUsd ?? 0,
      totalUsd: raw.totalUsd ?? 0,
      steps: (raw.steps ?? []).map((s) => ({ ...s, costUsd: s.costUsd ?? 0 })),
    };
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 404) return null;
    throw err;
  }
}

/** Approves or rejects a pending estimate; 403 = not an approver, 409 = no longer pending. */
export async function submitCostDecision(
  workflowId: string,
  body: CostDecisionRequest,
): Promise<CostEstimate> {
  return workflowRunsClient
    .post(endpoints.costEstimates.decision(workflowId), { json: body })
    .json<CostEstimate>();
}

/** Estimates matching the filters (e.g. pending, approver tier). */
export async function fetchCostEstimates(params: CostEstimateListParams): Promise<CostEstimate[]> {
  const searchParams: Record<string, string> = {};
  if (params.status) searchParams.status = params.status;
  if (params.tier) searchParams.tier = params.tier;
  return workflowRunsClient
    .get(endpoints.costEstimates.list(), { searchParams })
    .json<CostEstimate[]>();
}

/** Whether the caller is an environment-configured cost approver. */
export async function fetchCostApproverStatus(): Promise<CostApproverStatus> {
  return workflowRunsClient
    .get(endpoints.costEstimates.approverStatus())
    .json<CostApproverStatus>();
}

/**
 * One page of decided estimates (approved / rejected / expired / cancelled), newest decision
 * first. Anything but a page body is an error, not an empty page: a workflow-service without
 * the route answers `/history` as `/{workflowId}` (204, which ky reads as ''), and a missing
 * hosting rewrite serves HTML.
 */
export async function fetchCostHistory(params: CostHistoryParams): Promise<CostHistoryPage> {
  const searchParams: Record<string, string> = {
    status: params.statuses.join(','),
    page: String(params.page),
    size: String(params.size),
  };
  if (params.tier) searchParams.tier = params.tier;
  const raw = await workflowRunsClient
    .get(endpoints.costEstimates.history(), { searchParams })
    .json<CostHistoryPage | ''>();
  if (typeof raw !== 'object' || !Array.isArray(raw.items) || typeof raw.total !== 'number') {
    throw new Error('Cost approval history is unavailable (unexpected response).');
  }
  return raw;
}
